import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft } from 'lucide-react';
import { merchantsService } from '@/services/merchants';
import { courtsService } from '@/services/courts';
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
interface Court {
  id: number;
  name: string;
  status: string;
}

const Courts = () => {
  const [loading, setLoading] = useState(true);
  const { brandSlug } = useParams();
  const brandId = brandSlug?.split("-").pop();
  const navigate = useNavigate();
  const [courts, setCourts] = useState<Court[]>([]);
  const [brandName, setBrandName] = useState('');

  useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch brand
      const merchantRes = await merchantsService.fetchMerchants({ id: brandId });
      const brand = merchantRes.merchants.find(b => b.id == brandId);
      if (brand) {
        setBrandName(brand.name);
      }

      // Fetch courts
      const courtsRes = await courtsService.fetchCourtsByMerchant(brandId || '');
      const formattedCourts = courtsRes.courts.map(court => ({
        id: parseInt(court.id),
        name: court.name,
        status: court.status,
      }));

      setCourts(formattedCourts);

    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setTimeout(() => setLoading(false), 300); // smooth loading
    }
  };

  fetchData();
}, [brandId]);

  const handleCourtSelect = (courtName: string, courtId: number) => {
    const slugBrand = brandName.toLowerCase().replace(/\s+/g, "_");
    const slugCourt = courtName.toLowerCase().replace(/\s+/g, "_");
    navigate(`/brands/${slugBrand}-${brandId}/courts/${slugCourt}-${courtId}/videos`);
  };

  const CourtSkeleton = () => {
  return (
    <Card className="rounded-2xl border bg-white/80 backdrop-blur">
      <CardContent className="p-6 space-y-4">
        <div className="flex justify-between items-center">
          <Skeleton height={20} width="50%" />
          <Skeleton height={20} width={80} />
        </div>

        <Skeleton height={14} count={2} />

        <Skeleton height={40} />
      </CardContent>
    </Card>
  );
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-white to-green-100">
      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="mb-10">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/brands')}
            className="mb-4 rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Brands
          </Button>

          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            Courts at{" "}
            {loading ? (
              <Skeleton width={150} height={30} />
            ) : (
              brandName
            )}
          </h1>
          <p className="text-sm text-muted-foreground">
            Select a court to view available videos
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {loading
            ? Array.from({ length: 4 }).map((_, i) => (
                <CourtSkeleton key={i} />
              ))
            : courts.map((court) => (
            <Card
              key={court.id}
              onClick={court.status === "inactive" ? undefined : () => handleCourtSelect(court.name, court.id)}
              className={`
                rounded-2xl border bg-white/80 backdrop-blur
                transition-all duration-200
                ${court.status === "inactive"
                  ? "opacity-60 cursor-not-allowed"
                  : "cursor-pointer hover:shadow-lg hover:-translate-y-1"}
              `}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg font-semibold">
                    {court.name}
                  </h3>

                  {court.status === "inactive" ? (
                    <Badge variant="destructive">Unavailable</Badge>
                  ) : (
                    <Badge className="bg-green-600 text-white">Available</Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex justify-between">
                    <span>Video Quality</span>
                    <span className="font-medium text-foreground">HD 1080p</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Recording Hours</span>
                    <span className="font-medium text-foreground">08:00 – 22:00</span>
                  </div>
                </div>

                <Button
                  disabled={court.status === "inactive"}
                  className="w-full mt-6 rounded-xl"
                >
                  View Videos
                </Button>
              </CardContent>
            </Card>
          ))}
          {!loading && courts.length === 0 && (
            <div className="col-span-full text-center py-20">
              <h3 className="text-xl font-semibold mb-2">
                No courts available
              </h3>
              <p className="text-muted-foreground">
                This brand doesn’t have any courts available yet.
              </p>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div className="mt-14 rounded-2xl bg-white/80 backdrop-blur p-8 shadow-sm">
          <h3 className="text-lg font-semibold mb-6">
            Video Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-sm">
            <div>
              <h4 className="font-semibold mb-2 text-green-600">
                Free Videos
              </h4>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Available immediately after game</li>
                <li>Basic camera angles</li>
                <li>Download for 7 days</li>
              </ul>
            </div>

            {/* HIDE */}
            {/* <div>
              <h4 className="font-semibold mb-2 text-primary">
                Premium Videos
              </h4>
              <ul className="space-y-1 text-muted-foreground list-disc list-inside">
                <li>Multiple camera angles</li>
                <li>AI-powered highlights</li>
                <li>Unlimited downloads</li>
              </ul>
            </div> */}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Courts;